/**
 * Specialty-treatments resolver (single source of truth).
 *
 * Given the raw extras list returned by /api/specialty-extras for a
 * region, plus the customer's hasSaltSystem / hasSpa flags, return the
 * filtered list the customer actually sees on the quote page (and the
 * list that gets persisted into the signed agreement snapshot).
 *
 * Imported by:
 *   - src/Components/QuotePage.jsx (for the in-page agreement card)
 *   - api/quote/submit.js (for the immutable snapshot at sign time)
 */

/**
 * Per-deal specialty price override (rep-negotiated special rate,
 * e.g. a discounted spa drain). Operator-set on the stored quote as
 * `specialtyPriceOverrides: { [extraId]: price }`; survives publisher
 * republishes (see mergeQuoteForRepublish). Returns the override
 * price for an extra, or null when none applies.
 */
export function overriddenExtraPrice(extra, q) {
  const overrides = q?.specialtyPriceOverrides;
  if (!overrides || typeof overrides !== 'object') return null;
  const val = Number(overrides[extra?.id]);
  return Number.isFinite(val) && val > 0 ? val : null;
}

/**
 * True when the deal carries a standalone "Spa Only" service line
 * alongside pool service (mixed shape, e.g. deal 201540844). The
 * publisher CF emits an explicit `hasStandaloneSpa` flag; records
 * stored before that flag existed (or published by an older CF) fall
 * back to scanning the stored recurring service line names, so the
 * quote page and sign-time snapshot render the same regardless of
 * which side deployed first.
 */
export function quoteHasStandaloneSpa(q) {
  if (q?.hasStandaloneSpa !== undefined && q?.hasStandaloneSpa !== null) {
    return !!q.hasStandaloneSpa;
  }
  return Array.isArray(q?.serviceLineItems)
    && q.serviceLineItems.some((li) => /spa\s*only/i.test(String(li?.name || '')));
}

export function resolveSpecialtyExtras(rawExtras, q) {
  if (!Array.isArray(rawExtras)) return [];
  // Specialty treatments (PoolRX, Filter Clean, etc.) are recurring
  // add-ons to monthly weekly service. A one-time-only deal (a
  // diagnostic visit, a green-to-clean) doesn't have an ongoing service
  // schedule for them to attach to, so they shouldn't appear in the
  // quote, the snapshot, or the signed-agreement permalink.
  if (!q?.hasMonthlyService) return [];
  // Spa-only is signalled by the deal's service tier, the same way the
  // on-page agreement card derives it (QuotePage isSpaOnly). The old
  // `hasPool = true` hardcode made this always false, so any extra whose
  // conditional matched /spa only/ (e.g. Spa Drain, Clean & Fill) was
  // silently dropped from the signed snapshot even for spa-only deals.
  // hasStandaloneSpa covers the mixed shape (deal 201540844): pool
  // service plus a standalone "Spa Only" product line. The tier is
  // full (so Filter Clean and PoolRX apply to the pool), but the
  // standalone spa still gets drains, so spa-only-conditional extras
  // must stay visible.
  const isSpaOnly = q?.serviceTier === 'spa_only';
  const spaOnlyExtrasApply = isSpaOnly || quoteHasStandaloneSpa(q);
  return rawExtras
    .filter((e) => {
      if (e.notBilled) return false;
      // Annual heater tune-up moves to an onboarding upsell email,
      // so it shouldn't appear in the in-quote specialty list.
      if (e.id === 'annual-heater-tuneup') return false;
      const price = Number(e.price);
      const hasPrice = (e.priceLabel && String(e.priceLabel).trim().length > 0)
        || (Number.isFinite(price) && price > 0);
      if (!hasPrice) return false;
      const cond = (e.conditional || '').toString().toLowerCase();
      if (!cond) return true;
      if (/salt/.test(cond)) return !!q?.hasSaltSystem;
      if (/spa\s*only/.test(cond)) return spaOnlyExtrasApply;
      if (/spa/.test(cond)) return !!q?.hasSpa;
      return true;
    })
    .map((e) => {
      const override = overriddenExtraPrice(e, q);
      const price = override !== null ? override : (Number(e.price) || 0);
      const priceLabel = override !== null
        ? `$${override}`
        : (e.priceLabel || (Number.isFinite(Number(e.price)) && Number(e.price) > 0 ? `$${Number(e.price)}` : ''));
      return {
        id: e.id,
        name: e.name,
        frequency: e.frequency,
        price,
        priceLabel,
      };
    });
}
